import {FC, useMemo, useRef, useState} from 'react';
import {Box, Center, Flex} from '@chakra-ui/react';
import {api, EOAuthType, hasValue, useT} from '@kcast-fe/core';
import {EllipseButton, Loader, Modal} from 'ui-kit';

import {CredentialsForm, GroupedAccountList} from './components';

const MAX_WIDTH = {base: '796px', smToMd: 'calc(100% - 12px)'};

const ConnectionsSceneFC: FC = () => {
  const {data: integrations = [], isLoading} = api.oauth.useIntegrationsQuery();

  const [credentialsForm, setCredentialsForm] = useState<EOAuthType | null>(null);
  const [isVerifyFormShown, setIsVerifyFormShown] = useState(false);
  const [isTwoFactorShown, setIsTwoFactorShown] = useState(false);

  const ref = useRef<HTMLDivElement | null>(null);

  const {t} = useT();

  const hasAtLeastOneAccount = useMemo(() => {
    return integrations.some((i) => i.accounts.length > 0);
  }, [integrations]);

  const dialogTitle = useMemo(() => {
    if (!credentialsForm) {
      return t('common.connections');
    } else {
      return (
        <Flex alignItems={'center'} gap={'16px'}>
          <EllipseButton
            isRounded
            icon={'Back'}
            size={'normal'}
            iconSize={'s20'}
            variant={'light'}
            onClick={() => setCredentialsForm(null)}
          />
          <Box color={'{colors.textPrimary}'}>
            {isTwoFactorShown
              ? 'Two-factor authentication'
              : isVerifyFormShown
                ? 'Complete verification'
                : `Connect to ${t(`enums.oauthType.${credentialsForm}`)}`}
          </Box>
        </Flex>
      );
    }
  }, [credentialsForm, isVerifyFormShown, isTwoFactorShown, t]);

  return (
    <Modal isOpen title={dialogTitle} maxW={MAX_WIDTH}>
      <Flex
        ref={ref}
        overflow={'hidden'}
        p={'16px 0 0 0'}
        maxH={'calc(100dvh - 106px)'}
        flexDirection={'column'}
        gap={'16px'}
      >
        {isLoading ? (
          <Center h={'100px'}>
            <Loader size={'lg'} />
          </Center>
        ) : (
          <>
            {!hasValue(credentialsForm) ? (
              <GroupedAccountList
                ref={ref}
                integrations={integrations}
                hasAccounts={hasAtLeastOneAccount}
                onOpenCredentialsForm={setCredentialsForm}
              />
            ) : (
              <CredentialsForm
                service={credentialsForm}
                onIsVerification={setIsVerifyFormShown}
                onIsTwoFactor={setIsTwoFactorShown}
                onClose={() => setCredentialsForm(null)}
              />
            )}
          </>
        )}
      </Flex>
    </Modal>
  );
};

export const ConnectionsScene = ConnectionsSceneFC;
